import { useEffect, useMemo, useState } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertCircle,
  Copy,
  Download,
  Eye,
  ImageOff,
  Loader2,
  Printer,
  RefreshCw,
  Save,
  X,
} from "lucide-react";

import { alert } from "@/contexts/AlertModalContext";
import { useSocket } from "@/contexts/SocketContext";
import { useImpressaoConfig } from "@/contexts/ImpressaoConfigContext";
import type { ListaZplItem } from "@/types/lista-zpl-item";
import {
  dpmmDe,
  renderizarPreview,
  separarDownloadsGraficos,
} from "@/utils/zplDesigner";
import { preencherVariaveisExemplo } from "@/utils/variaveisZpl";
import { SeletorVariavel } from "./PainelPropriedades";

const PRESETS = [
  { rotulo: "100 × 50 mm", largura: "100", altura: "50" },
  { rotulo: "100 × 150 mm", largura: "100", altura: "150" },
  { rotulo: "60 × 40 mm", largura: "60", altura: "40" },
  { rotulo: "50 × 25 mm", largura: "50", altura: "25" },
  { rotulo: "33 × 22 mm", largura: "33", altura: "22" },
];

type Props = {
  valorInicial?: ListaZplItem | null;
  nomesExistentes: string[];
  salvando?: boolean;
  onSalvar: (item: ListaZplItem) => void;
  onCancelar: () => void;
  /** espelha o estado atual pro editor avançado continuar de onde parou */
  onRascunho?: (item: ListaZplItem) => void;
};

export function EditorSimples({
  valorInicial,
  nomesExistentes,
  salvando,
  onSalvar,
  onCancelar,
  onRascunho,
}: Props) {
  const { enviarMensagem, isConnected } = useSocket();
  const { impressaoConfig } = useImpressaoConfig();

  const [nome, setNome] = useState(valorInicial?.nome ?? "");
  const [largura, setLargura] = useState(valorInicial?.largura ?? "100");
  const [altura, setAltura] = useState(valorInicial?.altura ?? "50");
  const [dpi, setDpi] = useState(valorInicial?.dpmm ?? "203");
  const [zpl, setZpl] = useState(valorInicial?.zpl ?? "");
  const [erro, setErro] = useState<string | null>(null);

  const zplOriginal = valorInicial?.zpl ?? "";
  const zplIntocado = zpl === zplOriginal;

  const itemAtual = useMemo<ListaZplItem>(
    () => ({
      nome: nome.trim(),
      largura,
      altura,
      dpmm: dpi,
      zpl,
      // ZPL editado à mão invalida o desenho antigo — o avançado reimporta
      design: zplIntocado ? valorInicial?.design : undefined,
      zpl_manual: valorInicial?.zpl_manual,
    }),
    [nome, largura, altura, dpi, zpl, zplIntocado, valorInicial],
  );

  useEffect(() => {
    onRascunho?.(itemAtual);
  }, [itemAtual, onRascunho]);

  // ---- pré-visualização (POST: o GET estoura o limite de URL com logos) ----
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewErro, setPreviewErro] = useState<string | null>(null);
  const [carregando, setCarregando] = useState(false);
  const [gatilho, setGatilho] = useState(0);

  useEffect(() => {
    if (!zpl.trim() || !Number(largura) || !Number(altura)) {
      setPreviewUrl(null);
      return;
    }
    const controlador = new AbortController();
    const t = setTimeout(async () => {
      setCarregando(true);
      setPreviewErro(null);
      const r = await renderizarPreview(
        preencherVariaveisExemplo(zpl),
        {
          larguraMm: Number(largura),
          alturaMm: Number(altura),
          dpmm: dpmmDe(dpi),
        },
        controlador.signal,
      );
      if (controlador.signal.aborted) return;
      setCarregando(false);
      if (r.ok) {
        setPreviewUrl((antigo) => {
          if (antigo) URL.revokeObjectURL(antigo);
          return r.url;
        });
      } else if (r.erro) {
        setPreviewErro(r.erro);
      }
    }, 600);
    return () => {
      clearTimeout(t);
      controlador.abort();
    };
  }, [zpl, largura, altura, dpi, gatilho]);

  useEffect(
    () => () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const copiarZpl = async () => {
    try {
      await navigator.clipboard.writeText(zpl);
      alert.success("ZPL copiado.");
    } catch {
      alert.error("Não foi possível copiar.");
    }
  };

  const baixarZpl = () => {
    const blob = new Blob([zpl], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(nome || "etiqueta").replace(/[^\w-]+/g, "_")}.zpl`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const imprimirTeste = () => {
    if (!isConnected) {
      alert.error("Impressora não conectada.");
      return;
    }
    const delay = impressaoConfig?.tempo_delay_gravacao || "720";
    const tentativas = impressaoConfig?.tentativas_gravacao || "1";
    const forca = impressaoConfig?.potencia_padrao || "2";
    const inicioVirgem = impressaoConfig?.inicio_tag_virgem || "E280";
    const maiorEpc = impressaoConfig?.tag_virgem_maior_epc ? "true" : "false";
    const empacotar = (parte: string) =>
      `-${parte}|false|FFFFFFFFFFFFFFFFFFFFFFFF|${delay}|false|false|-|false|${tentativas}|false|${inicioVirgem}|${maiorEpc}|${forca}|true`;

    // ~DG/~DY tem que rodar fora do formato, antes do ^XA
    const { downloads, corpo } = separarDownloadsGraficos(
      preencherVariaveisExemplo(zpl),
    );
    const conteudo = corpo
      .replace(/\^XA/g, "")
      .replace(/\^XZ/g, "")
      .trim();

    enviarMensagem("FL");
    enviarMensagem(`P:${forca}`);
    for (let i = 0; i < downloads.length; i += 500) {
      enviarMensagem(empacotar(downloads.slice(i, i + 500)));
    }
    enviarMensagem(empacotar("^XA"));
    for (let i = 0; i < conteudo.length; i += 500) {
      enviarMensagem(empacotar(conteudo.slice(i, i + 500)));
    }
    enviarMensagem(empacotar("^XZ"));
    alert.success("Etiqueta de teste enviada à impressora.");
  };

  const salvar = () => {
    setErro(null);
    const nomeLimpo = nome.trim();
    if (!nomeLimpo) return setErro("Informe uma descrição para a etiqueta.");
    if (!Number(largura) || Number(largura) <= 0)
      return setErro("Largura deve ser maior que zero.");
    if (!Number(altura) || Number(altura) <= 0)
      return setErro("Altura deve ser maior que zero.");
    if (!zpl.trim()) return setErro("Informe o código ZPL da etiqueta.");
    if (
      nomesExistentes.some((n) => n.toLowerCase() === nomeLimpo.toLowerCase()) &&
      nomeLimpo.toLowerCase() !== (valorInicial?.nome ?? "").toLowerCase()
    )
      return setErro("Já existe uma etiqueta com esse nome.");

    onSalvar({ ...itemAtual, nome: nomeLimpo });
  };

  return (
    <Card>
      <CardContent className="p-4 space-y-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="grid gap-1.5 min-w-[220px] flex-1">
            <Label htmlFor="s-nome" className="text-[11px] text-muted-foreground">
              Descrição <span className="text-destructive">*</span>
            </Label>
            <Input
              id="s-nome"
              className="h-9"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              placeholder="Ex: Etiqueta Caixa 100x50"
            />
          </div>
          <div className="grid gap-1.5 w-28">
            <Label htmlFor="s-larg" className="text-[11px] text-muted-foreground">
              Largura (mm) <span className="text-destructive">*</span>
            </Label>
            <Input
              id="s-larg"
              className="h-9"
              type="number"
              min="1"
              value={largura}
              onChange={(e) => setLargura(e.target.value)}
            />
          </div>
          <div className="grid gap-1.5 w-28">
            <Label htmlFor="s-alt" className="text-[11px] text-muted-foreground">
              Altura (mm) <span className="text-destructive">*</span>
            </Label>
            <Input
              id="s-alt"
              className="h-9"
              type="number"
              min="1"
              value={altura}
              onChange={(e) => setAltura(e.target.value)}
            />
          </div>
          <div className="grid gap-1.5 w-24">
            <Label className="text-[11px] text-muted-foreground">DPI</Label>
            <Select value={dpi} onValueChange={setDpi}>
              <SelectTrigger className="h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="203">203</SelectItem>
                <SelectItem value="300">300</SelectItem>
                <SelectItem value="600">600</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-1.5 w-40">
            <Label className="text-[11px] text-muted-foreground">
              Tamanho comum
            </Label>
            <Select
              value=""
              onValueChange={(v) => {
                const p = PRESETS.find((x) => x.rotulo === v);
                if (!p) return;
                setLargura(p.largura);
                setAltura(p.altura);
              }}
            >
              <SelectTrigger className="h-9">
                <SelectValue placeholder="Selecionar" />
              </SelectTrigger>
              <SelectContent>
                {PRESETS.map((p) => (
                  <SelectItem key={p.rotulo} value={p.rotulo}>
                    {p.rotulo}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="ml-auto flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={onCancelar}>
              <X className="h-4 w-4" />
              Cancelar
            </Button>
            <Button size="sm" onClick={salvar} disabled={salvando}>
              {salvando ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              Salvar etiqueta
            </Button>
          </div>
        </div>

        <Separator />

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
          <div className="lg:col-span-3 space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="s-zpl" className="text-[11px] text-muted-foreground">
                Código ZPL <span className="text-destructive">*</span>
              </Label>
              <div className="flex items-center gap-1">
                <SeletorVariavel
                  onInserir={(t) => setZpl((atual) => atual + t)}
                />
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 text-[11px]"
                  onClick={copiarZpl}
                >
                  <Copy className="h-3.5 w-3.5" />
                  Copiar
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 text-[11px]"
                  onClick={baixarZpl}
                >
                  <Download className="h-3.5 w-3.5" />
                  .zpl
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 text-[11px]"
                  onClick={imprimirTeste}
                  disabled={!isConnected}
                  title={
                    isConnected
                      ? "Imprime uma etiqueta de teste sem gravar RFID"
                      : "Impressora desconectada"
                  }
                >
                  <Printer className="h-3.5 w-3.5" />
                  Teste
                </Button>
              </div>
            </div>
            <Textarea
              id="s-zpl"
              className="h-[440px] text-[11px] font-mono resize-none"
              value={zpl}
              onChange={(e) => setZpl(e.target.value)}
              placeholder="^XA ... ^XZ"
              spellCheck={false}
            />
            <p className="text-[10px] text-muted-foreground">
              {zpl.length.toLocaleString("pt-BR")} caracteres
            </p>

            {erro && (
              <div
                role="alert"
                className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-xs text-destructive dark:text-red-400"
              >
                <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
                <span>{erro}</span>
              </div>
            )}
          </div>

          <div className="lg:col-span-2 space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-[11px] text-muted-foreground">
                Pré-visualização ao vivo
              </Label>
              <Button
                size="icon"
                variant="ghost"
                className="h-7 w-7"
                onClick={() => setGatilho((g) => g + 1)}
                disabled={carregando}
              >
                <RefreshCw
                  className={`h-3.5 w-3.5 ${carregando ? "animate-spin" : ""}`}
                />
              </Button>
            </div>
            <div className="relative flex min-h-[300px] items-center justify-center overflow-auto rounded-md border bg-white p-2">
              {previewUrl ? (
                <img
                  src={previewUrl}
                  alt="Pré-visualização"
                  className="max-w-full"
                />
              ) : previewErro ? (
                <div className="flex flex-col items-center gap-2 p-4 text-center text-muted-foreground">
                  <ImageOff className="h-8 w-8 opacity-50" />
                  <p className="text-xs">Não foi possível renderizar.</p>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-2 p-4 text-center text-muted-foreground">
                  <Eye className="h-8 w-8 opacity-40" />
                  <p className="text-xs">
                    {carregando
                      ? "Renderizando..."
                      : "Preencha tamanho e ZPL para ver a pré-visualização."}
                  </p>
                </div>
              )}
              {carregando && previewUrl && (
                <div className="absolute inset-0 flex items-center justify-center bg-white/60">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                </div>
              )}
            </div>
            {previewErro && (
              <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-2 text-[11px] text-destructive dark:text-red-400">
                <AlertCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                <span className="break-words">{previewErro}</span>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
